import React from 'react';
import {
  Component,
  PuzzleButton,
  Container,
  PuzzleColorPicker, PuzzleTabStrip, FORM_CONTROL
} from "puzzle";
import {
  Editor,
  EditorTools,
  EditorUtils,
  EditorProps,
} from '@progress/kendo-react-editor';
import _, { debounce } from "lodash";
import ReferenceFiles from "./ReferenceFiles/ReferenceFiles";
import Policies from "../../../util/Policies";
import FontSizeTool from "puzzle/components/UI/rich_text_editor/font/font_size_tool/FontSizeTool";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import { EditorChangeEvent } from "@progress/kendo-react-editor/dist/npm/EditorProps";
import { INSTRUCTIONS_DATA } from "../../Types";
import { REFERENCE_FILE } from "../../../MainGrid/PreviewTemplates/Types";

const {
  Bold, Italic, Underline,
  AlignLeft, AlignCenter, AlignJustify,
  OrderedList, UnorderedList
} = EditorTools;

interface PROPS extends EditorProps {
  form: FORM_CONTROL,
  submissionStatus: string
}

export type STATE = {
  instructionsData: string,
  referenceFiles: REFERENCE_FILE[],
  deletedReferenceFiles: REFERENCE_FILE[],
  notesData: string,
  selectedTabStripTab: number,
  notesTabSelected: boolean, // this property is required so code is more understandable. We could use selectedTabStripTab, but it would be confusing with multiple tabs
  instructionsHtmlText: any,
  notesHtmlText: string,
  EditorMountEvent: any,
  isPlaceholderVisible: boolean,
  submissionId: string
};

class Instructions extends Component<PROPS, STATE> {
  submissionId: string = '';
  editorRef: any;
  tabStripRef: any;
  instructionsWrapperRef: any;

  static defaultProps = {
    submissionStatus: ''
  };
  public state: STATE = {
    instructionsData: '',
    referenceFiles: [],
    deletedReferenceFiles: [],
    notesData: '',
    selectedTabStripTab: 0,
    notesTabSelected: false,
    instructionsHtmlText: '',
    notesHtmlText: '',
    EditorMountEvent: null,
    isPlaceholderVisible: true,
    submissionId: 0
  };

  /**
   * Get data from editor
   */
  getData = () => {
    const {
      deletedReferenceFiles,
      referenceFiles,
      instructionsHtmlText,
      notesHtmlText,
      notesTabSelected,
      selectedTabStripTab
    } = this.state;

    let data = {
      instructionsHtmlText,
      notesHtmlText,
      referenceFiles,
      deletedReferenceFiles,
      notesTabSelected,
      selectedTabStripTab
    };
    return data;
  };

  /**
   * @inheritDoc
   */
  moduleDidMount() {
    // Have to do this this way in case something changes and if notes tab should be open by default.
    this.setPlaceholderTextValue();
  }

  /**
   * @inheritDoc
   */
  componentDidUpdate(prevProps: PROPS, prevState: STATE) {
    const { notesTabSelected } = this.state;
    if (prevState.notesTabSelected !== notesTabSelected) {
      this.setPlaceholderTextValue();
      this.updateHtmlEditorText();
    }
  }

  /**
   * This will not show placeholder it will just change the text(value) of it.
   */
  setPlaceholderTextValue() {
    const keyForTranslation: string = this.state.notesTabSelected ? 'enterTheNotesHere' : 'enterTheInstructionsHere';
    this.editorRef.element.querySelector('div.ProseMirror')?.setAttribute('data-placeholder', this.translate(keyForTranslation));
  }

  /**
   * Saves Instructions or Notes text to state.
   * Used on multiple places and also solves a bug where text is not saved if user loses focus due to placeholder and onMount function
   * @param event
   */
  saveTextToState = (event: EditorChangeEvent) => {
    const { html } = event;
    let newState = {
      ...this.state,
      ...(this.state.notesTabSelected ? { notesHtmlText: html } : { instructionsHtmlText: html })
    };

    this.setState({ ...newState });
  }

  /**
   * Set uploaded files into the state for POST
   * @param referenceFiles
   * @param deletedReference
   */
  onUploadChange = (referenceFiles: REFERENCE_FILE[], deletedReference: REFERENCE_FILE[]) => {
    this.props.form.setData({ referenceFiles });
    this.setState({
      referenceFiles: _.cloneDeep(referenceFiles),
      deletedReferenceFiles: _.cloneDeep(deletedReference)
    });
  };

  /**
   * Set data
   * @param data
   */
  setData = (data: INSTRUCTIONS_DATA) => {
    const { instructionsHtmlText, notesHtmlText, referenceFiles } = data;
    this.props.form.setData({
      instructions: instructionsHtmlText,
      notes: notesHtmlText,
      referenceFiles
    });

    this.setState({
      ...data
    }, () => {
      this.submissionId = data.submissionId;
      this.updateHtmlEditorText();
    });
  };

  /**
   * Handling placeholder and calling save State with delay for performance issues
   * @param event
   */
  onInputChange = (event: EditorChangeEvent) => {
    let isPlaceholderVisible = false;
    let inputValue: string | null = event.html;
    let selectedTab = this.state.notesTabSelected ? 'notes' : 'instructions';

    if (event.html === "<p></p>" || event.html === '') {
      isPlaceholderVisible = true;
      inputValue = null;
    }

    if (!this.state.isPlaceholderVisible === isPlaceholderVisible) {
      this.setState({ isPlaceholderVisible });
    }

    this.props.form.setData({ [selectedTab]: inputValue });
    this.delayedSaveState(event);
  }

  /**
   * delayed saveTextToState
   * Put to 300 because on 500 user can type text and switch tabs and it won't save
   */
  delayedSaveState = debounce(this.saveTextToState, 300);

  /**
   * Only updates text on Editor (Best example and where it is mostly called is when you are switching from Notes to Instructions and vice versa)
   */
  updateHtmlEditorText = () => {
    const { instructionsHtmlText, notesTabSelected, notesHtmlText } = this.state;
    const editorView = this.editorRef.view;
    const htmlText = notesTabSelected ? notesHtmlText : instructionsHtmlText;
    EditorUtils.setHtml(editorView, htmlText || '');
  }

  renderBody() {
    const { notesTabSelected } = this.state;
    const { project } = this.props.form.getData();
    const hasSubmissionReferenceFilesPolicy = Policies.inProject(project)?.hasSubmissionReferenceFilesPolicy;

    return (
      <Container
        vBox
        flex1
        className='tp-section-container tp-instructions-and-reference-files-section'
        ref={wrapper => this.instructionsWrapperRef = wrapper}
      >
        <Container className={'tp-instructions-section'}>
          <Container hBox>
            <Container
              htmlTag={'h3'}
              hBox
              alignItemsCenter
              justifyContentBetween
              className="tp-section-container-title tp-font-size-md tp-text-semibold"
            >
              {this.translate('instructions')}
            </Container>
            <PuzzleButton
              aria-label={this.translate('SRL-openInstructionInModal')}
              titleText={this.translate('SRL-openInstructionInModal')}
              data-cy={this.translate('SRL-openInstructionInModal')}
              onClick={this.openPopupDialogue}
              iconClass={'tp-icon tp-open_in_new'}
              className={project ? '' : 'k-state-disabled'}
              disabled={!project}
              fillMode='flat'
              themeColor={'primary'}
              tooltipPosition={'right'}
              withTooltip />
          </Container>
          <div className={'tp-section-container-content'}>
            <div className={'tp-section-container-content-inner-wrap'}>
              <Editor
                ref={editor => this.editorRef = editor}
                ariaLabel={this.translate(notesTabSelected ? "enterTheNotesHere" : "enterTheInstructionsHere")}
                contentStyle={{ height: '180px' }}
                className={`tp-section-instructions-editor k-vbox tp-no-border ${this.state.isPlaceholderVisible ? 'tp-empty' : ''}`}
                tools={[
                  [Bold, Italic, Underline],
                  [UnorderedList, OrderedList],
                  [AlignCenter, AlignJustify, AlignLeft],
                  [FontSizeTool],
                  [PuzzleColorPicker],
                  [this.tabStripFunctionComponent]
                ]}
                onChange={this.onInputChange}
                defaultEditMode={'div'}
              />
            </div>
          </div>
        </Container>
        {hasSubmissionReferenceFilesPolicy &&
          <div className={'tp-reference-files-section'}>
            <ReferenceFiles
              moduleId={'fromInstructions'}
              onChange={this.onUploadChange}
              submissionId={this.submissionId}
              submissionStatus={this.props.submissionStatus}
            />
          </div>}
      </Container>
    );
  }

  /**
   * Opens popup Dialogue when you press on that little arrow near Editor component
   */
  openPopupDialogue = () => {
    this.dispatch('openSubmissionPopup', '', {
      form: {
        ...this.props.form,
        submissionId: this.submissionId
      },
      popupName: 'instructions',
      instructionsData: this.getData()
    })
  }


  /**
   * Handles change of TabStrip selection
   * @param e
   */
  handleSelect = (e: TabStripSelectEventArguments) => {
    this.setState({ selectedTabStripTab: e.selected, notesTabSelected: e.selected === 1 });
  };

  /**
   * A TabStrip function component that is created to be used as a tool in tools array on Editor component
   */
  tabStripFunctionComponent = () => {
    return (
      <PuzzleTabStrip
        ref={(tabStrip: any) => this.tabStripRef = tabStrip}
        handleSelect={this.handleSelect}
        tabCount={2}
        translate={this.translate}
        selected={this.state.selectedTabStripTab}
      >
        <TabStrip
          selected={this.state.selectedTabStripTab}
        >
          <TabStripTab
            key={"instructions"}
            title={this.translate("instructionsTabStrip")}
            contentClassName="tp-display-none"
          >
          </TabStripTab>
          <TabStripTab
            key={"notes"}
            title={this.translate("notesTabStrip")}
            contentClassName="tp-display-none"
          >
          </TabStripTab>
        </TabStrip>
      </PuzzleTabStrip>
    );
  }
}

export default Instructions;
